import React, { Dispatch, SetStateAction } from 'react'
import { OneBlinkAppsError } from '@oneblink/apps-react'

import useOneBlinkError, { SetErrorFn } from '../hooks/useOneBlinkError'
import { searchRequest, HTTPError } from '../lib/http'

import config from '../config'

interface State {
  isFetching: boolean
  approvalData?: Record<string, unknown>
  fetchErrorCode?: number
}

export default function useGetClarification(): [
  State & { fetchErrorMessage?: OneBlinkAppsError },
  {
    setState: Dispatch<SetStateAction<State>>
    fetchClarification: ({
      previousFormSubmissionApprovalId,
      externalId,
    }: {
      previousFormSubmissionApprovalId: string
      externalId: string
    }) => Promise<State['approvalData'] | void>
    setFetchErrorMessage: SetErrorFn
  },
] {
  const [state, setState] = React.useState<State>({ isFetching: false })

  const [fetchErrorMessage, setFetchErrorMessage] = useOneBlinkError()

  const fetchClarification = React.useCallback(
    async ({
      previousFormSubmissionApprovalId,
      externalId,
    }: {
      previousFormSubmissionApprovalId: string
      externalId: string
    }) => {
      setState({ isFetching: true })
      setFetchErrorMessage(undefined)
      try {
        const approvalData = await searchRequest<Record<string, unknown>>({
          url: `${config.HOSTED_API_URL}/submissions/approvals/clarification`,
          searchParameters: { previousFormSubmissionApprovalId, externalId },
          addAuthHeaders: true,
        })

        setState({ isFetching: false, approvalData })
        return approvalData
      } catch (e) {
        let fetchErrorCode
        if (e instanceof HTTPError && (e.status === 403 || e.status === 404)) {
          fetchErrorCode = e.status
        }
        setFetchErrorMessage(e as Error)
        setState({ isFetching: false, fetchErrorCode })
      }
    },
    [setFetchErrorMessage, setState],
  )

  return [
    { fetchErrorMessage, ...state },
    { fetchClarification, setState, setFetchErrorMessage },
  ]
}
